import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { RbacProvider } from './admin/rbac/RbacProvider';
import theme from './admin/theme';
import { installDateGuard } from './utils/dateGuard';

/* ──────────────────────────────────────────────────────────────
   Global fix: mouse-wheel must NOT change <input type="number">.
   When the wheel turns over a focused number field we blur it, so
   the page scrolls and the typed value (e.g. 1000) stays intact.
─────────────────────────────────────────────────────────────── */
document.addEventListener(
  'wheel',
  () => {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type === 'number') {
      el.blur();
    }
  },
  { passive: true },
);

/* ──────────────────────────────────────────────────────────────
   Global fix: truncated Select / Autocomplete / read-only input
   shows its full text as a native browser tooltip on hover.
─────────────────────────────────────────────────────────────── */
document.addEventListener('mouseover', (e) => {
  if (!(e.target instanceof HTMLElement)) return;
  const sel = e.target.closest(
    '.MuiSelect-select, .MuiInputBase-input, .MuiAutocomplete-input',
  );
  if (!sel) return;
  const text = (sel.value ?? sel.textContent ?? '').toString().trim();
  if (!text) return;
  if (sel.scrollWidth > sel.clientWidth + 1) {
    if (sel.title !== text) sel.title = text;
  } else if (sel.title) {
    sel.removeAttribute('title');
  }
});

/* ──────────────────────────────────────────────────────────────
   Autocomplete <input>: HTML inputs can't wrap. When the user
   tabs/clicks away we reset the scroll so it shows the START of
   the value (e.g. "Rajas...") instead of the end ("than"), which
   pairs with the CSS text-overflow: ellipsis from the theme.
─────────────────────────────────────────────────────────────── */
document.addEventListener(
  'focusout',
  (e) => {
    const t = e.target;
    if (t instanceof HTMLInputElement && t.matches('.MuiAutocomplete-input')) {
      // Defer so MUI's own blur handling finishes first.
      setTimeout(() => { t.scrollLeft = 0; }, 0);
    }
  },
  true,
);

/* ──────────────────────────────────────────────────────────────
   Runtime style injection. MUI's @emotion injects component styles
   at runtime  sometimes AFTER our CSS  which can defeat even
   !important rules from index.css. Appending these rules to <head>
   AT THE END guarantees they win the cascade for every dropdown
   in the project, no matter when MUI mounts.
─────────────────────────────────────────────────────────────── */
const dropdownFix = document.createElement('style');
dropdownFix.setAttribute('data-app', 'dropdown-fix');
dropdownFix.textContent = `
  /* Closed Select  wrap selected value */
  body .MuiSelect-select,
  body .MuiInputBase-root .MuiSelect-select {
    white-space: normal !important;
    word-break: break-word !important;
    text-overflow: clip !important;
    overflow: visible !important;
    min-height: 0 !important;
    line-height: 1.3 !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }

  /* Open menu items  wrap + tighter right padding so the last word
     ("Islands") never gets clipped at the right edge. */
  body .MuiMenuItem-root,
  body .MuiAutocomplete-option {
    white-space: normal !important;
    word-break: break-word !important;
    line-height: 1.35 !important;
    min-height: 0 !important;
    padding-right: 10px !important;
    overflow: visible !important;
    text-overflow: clip !important;
  }

  /* Autocomplete input  show start of value with ellipsis */
  body .MuiAutocomplete-input {
    text-overflow: ellipsis !important;
  }

  /* Menu / Autocomplete popup paper  width OVERRIDES MUI's default
     of matching the anchor field width. Targets every variant:
     MuiMenu (Select), MuiAutocomplete-paper, MuiPopover, popper child. */
  body .MuiMenu-paper,
  body .MuiAutocomplete-paper,
  body .MuiPopover-paper,
  body .MuiAutocomplete-popper > .MuiPaper-root,
  body .MuiAutocomplete-popper > div {
    max-height: 380px !important;
    min-width: 260px !important;
    width: max-content !important;
    max-width: 420px !important;
    overflow: hidden !important;
  }

  /* List inside the paper  match width so items can use full space. */
  body .MuiMenu-list,
  body .MuiAutocomplete-listbox {
    min-width: 260px !important;
    max-width: 420px !important;
    max-height: 360px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding-right: 0 !important;
  }

  /* If MUI sets an inline width on the popper, force max-content. */
  body .MuiAutocomplete-popper {
    width: max-content !important;
    min-width: 260px !important;
    max-width: 420px !important;
  }
`;
// Append after a microtask so MUI's emotion <style> tags are already in the DOM.
queueMicrotask(() => document.head.appendChild(dropdownFix));

/* ──────────────────────────────────────────────────────────────
   Global fix: reject absurd years in <input type="date"> (e.g. a
   typed 0206). Installed here at startup — same pattern as the
   listeners above — so it covers every date field app-wide and
   does not depend on any React component lifecycle.
─────────────────────────────────────────────────────────────── */
installDateGuard();

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <RbacProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </RbacProvider>
    </BrowserRouter>
  </React.StrictMode>,
);

reportWebVitals();
